import type { ApplyResult, PublicJobInfo } from '@eduvy/core';
import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { Icon, type IconName } from '../components/icons';
import { Button, Callout, Checkbox, Field, Input, Loading, LogoMark, cx } from '../components/ui';

type Load = { kind: 'loading' } | { kind: 'ready'; info: PublicJobInfo } | { kind: 'error'; message: string };

/** +918035088055 -> +91 80 3508 8055; mobiles -> +91 98450 12345. */
function prettyPhone(e164: string): string {
  const m = /^\+91(\d{10})$/.exec(e164);
  if (!m) return e164;
  const d = m[1]!;
  return /^[6-9]/.test(d) ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : `+91 ${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6)}`;
}

/**
 * A job's public apply page (/apply/:job/:key). The candidate adds themselves, with a resume, and
 * is interviewed at once: a link to start in the browser, or a call within calling hours.
 */
export function ApplyPage() {
  const { job, key } = useParams() as { job: string; key: string };
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [result, setResult] = useState<ApplyResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/public/apply/${encodeURIComponent(job)}/${encodeURIComponent(key)}`)
      .then(async (r) => {
        const body = (await r.json().catch(() => ({}))) as PublicJobInfo & { error?: string };
        if (!r.ok) throw new Error(body.error ?? 'This link is not valid.');
        if (!cancelled) setLoad({ kind: 'ready', info: body });
      })
      .catch((err: unknown) => !cancelled && setLoad({ kind: 'error', message: err instanceof Error ? err.message : 'This link is not valid.' }));
    return () => {
      cancelled = true;
    };
  }, [job, key]);

  if (load.kind === 'loading') return <Loading className="min-h-full" />;
  if (load.kind === 'error') return <Shell><Callout tone="warning">{load.message}</Callout></Shell>;
  const info = load.info;

  return (
    <Shell company={info.company}>
      <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:gap-12">
        <section className="min-w-0">
          <p className="eyebrow">{info.company} is hiring</p>
          <h1 className="display mt-2 text-[34px] font-medium leading-[1.08] text-fg sm:text-[44px]">{info.jobTitle}</h1>
          <div className="mt-4 flex flex-wrap gap-2">
            <Chip icon="pin">{info.location}</Chip>
            <Chip icon="building">{info.workMode}</Chip>
            <Chip icon="clock">{info.minutes}-minute AI interview</Chip>
            <Chip icon="language">{info.languages.map((l) => l.native).join(' · ')}</Chip>
          </div>
          {info.description && <p className="mt-6 max-w-prose whitespace-pre-line text-[15px] leading-relaxed text-fg-2">{info.description}</p>}
          {info.note && (
            <Callout tone="brand" icon="info" className="mt-5 max-w-prose">
              {info.note}
            </Callout>
          )}
          <ol className="mt-8 grid gap-3 sm:grid-cols-3">
            {[
              ['1', 'Apply', 'Your details and your resume, two minutes.'],
              ['2', info.how === 'link' ? 'Interview now' : 'We call you', info.how === 'link' ? 'A short voice interview in your browser, in the language you choose.' : `Our AI interviewer calls you within calling hours, in the language you choose.`],
              ['3', 'The team reviews', 'A recruiter reads every interview and gets back to you.'],
            ].map(([n, title, body]) => (
              <li key={n} className="surface rounded-lg p-4">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-gradient font-mono text-[11px] font-semibold text-white">{n}</span>
                <p className="mt-3 text-sm font-semibold text-fg">{title}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-fg">{body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="min-w-0">
          {result ? <Outcome result={result} info={info} /> : info.accepting ? <ApplyForm job={job} apiKey={key} info={info} onDone={setResult} /> : <Callout tone="info">This job is not taking applications right now.</Callout>}
        </section>
      </div>
    </Shell>
  );
}

function Shell({ company, children }: { company?: string; children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-bg bg-brand-wash">
      <header className="mx-auto flex max-w-6xl items-center gap-3 px-4 pt-6 sm:px-8">
        <LogoMark className="h-8 w-8" />
        <span className="display text-lg font-semibold text-fg">Eduvy</span>
        {company && <span className="ml-auto text-sm text-muted-fg">Hiring for {company}</span>}
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-8 lg:py-12">{children}</main>
      <footer className="mx-auto max-w-6xl px-4 pb-10 text-xs text-faint sm:px-8">Your details are used for this hiring process only. You can ask the company to delete them at any time.</footer>
    </div>
  );
}

function Chip({ icon, children }: { icon: IconName; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-surface px-3 py-1 text-xs font-medium text-fg-2 ring-1 ring-inset ring-border">
      <Icon name={icon} className="h-3.5 w-3.5 text-accent" />
      {children}
    </span>
  );
}

function ApplyForm({ job, apiKey, info, onDone }: { job: string; apiKey: string; info: PublicJobInfo; onDone: (r: ApplyResult) => void }) {
  const [f, setF] = useState({ name: '', phone: '', email: '', experience: '', sector: '', location: '' });
  const [resume, setResume] = useState<File | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!agreed) return setError('Please agree to the interview terms.');
    setBusy(true);
    setError(null);
    const body = new FormData();
    for (const [k, v] of Object.entries(f)) body.append(k, v);
    body.append('consent', 'yes');
    if (resume) body.append('resume', resume, resume.name);
    try {
      const r = await fetch(`/api/public/apply/${encodeURIComponent(job)}/${encodeURIComponent(apiKey)}`, { method: 'POST', body });
      const json = (await r.json().catch(() => ({}))) as ApplyResult & { error?: string };
      if (!r.ok) throw new Error(json.error ?? 'Something went wrong. Please try again.');
      onDone(json);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="surface scale-in rounded-xl p-5 sm:p-6">
      <h2 className="display text-2xl font-medium text-fg">Apply</h2>
      <p className="mt-1 text-sm text-muted-fg">{info.how === 'link' ? 'Your interview starts right after, so pick a quiet moment.' : 'Our interviewer calls the number you give.'}</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Field label="Your name" className="sm:col-span-2">
          <Input required minLength={2} value={f.name} onChange={set('name')} autoComplete="name" leading={<Icon name="user" className="h-4 w-4" />} />
        </Field>
        <Field label="Mobile number" hint="We call or message this number.">
          <Input required inputMode="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" placeholder="98450 12345" leading={<Icon name="phone" className="h-4 w-4" />} />
        </Field>
        <Field label="Email" optional>
          <Input type="email" value={f.email} onChange={set('email')} autoComplete="email" leading={<Icon name="mail" className="h-4 w-4" />} />
        </Field>
        <Field label="Years of experience" hint="0 if you are a fresher.">
          <Input inputMode="decimal" value={f.experience} onChange={set('experience')} placeholder="e.g. 2" />
        </Field>
        <Field label="Current role or industry" optional>
          <Input value={f.sector} onChange={set('sector')} placeholder="e.g. Customer support, EdTech" />
        </Field>
        <Field label="City" optional className="sm:col-span-2">
          <Input value={f.location} onChange={set('location')} autoComplete="address-level2" leading={<Icon name="pin" className="h-4 w-4" />} />
        </Field>
        <Field label="Resume" optional hint="PDF or text, up to 5 MB. The interviewer reads it before you talk." className="sm:col-span-2" as="div">
          <label className={cx('flex cursor-pointer items-center gap-3 rounded-sm border border-dashed px-3.5 py-3 text-sm transition', resume ? 'border-accent bg-accent-soft text-fg' : 'border-border-strong bg-surface-2 text-muted-fg hover:border-accent/60')}>
            <input type="file" accept=".pdf,.txt,application/pdf,text/plain" className="sr-only" onChange={(e) => setResume(e.target.files?.[0] ?? null)} />
            <Icon name={resume ? 'check' : 'upload'} className="h-4 w-4 shrink-0 text-accent" />
            <span className="min-w-0 truncate">{resume ? resume.name : 'Choose a file'}</span>
          </label>
        </Field>
        {/* Honeypot: people never see it, bots fill it. */}
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-px w-px opacity-0" onChange={() => undefined} />
      </div>
      <div className="mt-5">
        <Checkbox checked={agreed} onChange={setAgreed} hint={`The interview is conducted by an AI, recorded (audio and video) and assessed, and ${info.company}'s hiring team reviews it.`}>
          I agree to the interview terms
        </Checkbox>
      </div>
      {error && (
        <Callout tone="danger" className="mt-4">
          {error}
        </Callout>
      )}
      <Button type="submit" variant="primary" size="lg" className="mt-5 w-full" loading={busy} iconRight="arrowRight">
        {info.how === 'link' ? 'Apply and start my interview' : 'Apply'}
      </Button>
    </form>
  );
}

function Outcome({ result, info }: { result: ApplyResult; info: PublicJobInfo }) {
  const tone = result.outcome === 'link' || result.outcome === 'call' ? 'success' : 'info';
  return (
    <div className="surface scale-in rounded-xl p-6 sm:p-8">
      <span className={cx('inline-flex h-12 w-12 items-center justify-center rounded-full', tone === 'success' ? 'bg-brand-gradient text-white shadow-brand' : 'bg-accent-soft text-accent')}>
        <Icon name={result.outcome === 'call' ? 'phone' : 'check'} className="h-6 w-6" strokeWidth={2.2} />
      </span>
      <h2 className="display mt-5 text-2xl font-medium text-fg">{result.outcome === 'already' ? 'You have already applied' : result.outcome === 'queued' ? 'Application received' : result.outcome === 'call' ? 'We will call you' : 'Your interview is ready'}</h2>
      <p className="mt-2 text-[15px] leading-relaxed text-fg-2">{result.message}</p>
      {result.link && (
        <>
          <Button variant="primary" size="lg" className="mt-6 w-full" icon="headphones" onClick={() => window.location.assign(result.link!)}>
            Start my interview
          </Button>
          <p className="mt-3 text-xs text-muted-fg">About {info.minutes} minutes. Use headphones if you can; you can choose the language at the start.</p>
        </>
      )}
      {result.callIn && (
        <a href={`tel:${result.callIn}`} className="mt-6 flex items-center gap-3 rounded-md bg-surface-2 px-4 py-3 ring-1 ring-border transition hover:ring-accent">
          <Icon name="phone" className="h-5 w-5 text-accent" />
          <span>
            <span className="block text-sm font-semibold text-fg">Call {prettyPhone(result.callIn)}</span>
            <span className="block text-xs text-muted-fg">to take the interview right away</span>
          </span>
        </a>
      )}
    </div>
  );
}
